import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function Header() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const languages = [
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "da", label: "Dansk" },
    { code: "de", label: "Deutsch" },
  ];

  const current = i18n.language || "en";

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".lang-switcher")) setLangOpen(false);
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  const go = (path: string) => {
    setMobileOpen(false);
    setLangOpen(false);
    navigate(path);
  };

  const handleLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => go("/")}
              className="text-xl font-bold text-foreground-950 tracking-tight hover:text-primary-500 transition-colors whitespace-nowrap"
            >
              AllChinaBuy
            </button>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-6">
              <button
                onClick={() => go("/")}
                className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                {t("nav.home")}
              </button>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                {t("nav.shopGuide")}
              </a>
              <button
                onClick={() => go("/allchinabuy-spreadsheet")}
                className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                {t("nav.spreadsheet")}
              </button>

              <div className="relative lang-switcher">
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className="flex items-center gap-1.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors px-3 py-1.5 rounded-md border border-background-200/70 bg-white"
                >
                  <i className="ri-global-line text-base" />
                  <span className="uppercase">{current}</span>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? "rotate-180" : ""}`} />
                </button>
                {langOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-background-200/70 z-50 min-w-[140px] py-1">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLang(lang.code)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors ${
                          current === lang.code ? "text-primary-500 font-medium" : "text-foreground-700"
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </nav>

            {/* Mobile controls */}
            <div className="flex md:hidden items-center gap-2">
              <div className="relative lang-switcher">
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className="flex items-center justify-center w-10 h-10 rounded-md border border-background-200/70 bg-white text-foreground-700"
                  aria-label="Language"
                >
                  <i className="ri-global-line text-lg" />
                </button>
                {langOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-background-200/70 z-50 min-w-[140px] py-1">
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLang(lang.code)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors ${
                          current === lang.code ? "text-primary-500 font-medium" : "text-foreground-700"
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="flex items-center justify-center w-10 h-10 rounded-md border border-background-200/70 bg-white text-foreground-900"
                aria-label="Menu"
              >
                <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-white overflow-y-auto">
            <div className="px-4 py-6 space-y-2">
              <button
                onClick={() => go("/")}
                className="w-full text-left flex items-center gap-3 py-3 text-base font-semibold text-foreground-900"
              >
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-home-5-line text-lg" />
                </span>
                {t("nav.home")}
              </button>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 py-3 text-base font-semibold text-foreground-900"
              >
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-shopping-bag-3-line text-lg" />
                </span>
                {t("nav.shopGuide")}
              </a>
              <button
                onClick={() => go("/allchinabuy-spreadsheet")}
                className="w-full text-left flex items-center gap-3 py-3 text-base font-semibold text-foreground-900"
              >
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <i className="ri-table-line text-lg" />
                </span>
                {t("nav.spreadsheet")}
              </button>

              <div className="pt-4 mt-2 border-t border-background-200/70">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3 px-1">
                  {t("categories.title")}
                </h4>
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat, idx) => (
                    <a
                      key={idx}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-background-100 transition-colors"
                    >
                      <span className="w-7 h-7 flex items-center justify-center rounded bg-primary-50 text-primary-500 shrink-0">
                        <i className={`${cat.icon} text-sm`} />
                      </span>
                      <span className="text-sm text-foreground-700 whitespace-nowrap">
                        {t(cat.nameKey)}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}